import { Button, Modal, TextInput } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "../Forms/components/ButtonMenu.js";
import { z } from "zod";
import { useForm } from "@mantine/form";
import validateAsync from "@/utils/ZodHelpers/validateAsync.js";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import { userApi } from "@/api/index.js";
import { User } from "@/types/Users/Users.js";
import LoggingAPI from "@/api/logging/index.js";

interface ChangePasswordModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onCancel: () => void;
  defaultInfo: User;
}

const passwordValidationSchema = z.object({
  password: z
    .string()
    .min(7, { message: "Must be 7 or more characters" })
    .refine(
      (password) => /[a-z]/.test(password),
      "Must contain lowercase letter"
    )
    .refine(
      (password) => /[A-Z]/.test(password),
      "Must contain uppercase letter"
    )
    .refine((password) => /[0-9]/.test(password), "Must contain number")
    .refine(
      (password) => /[!+_\-\.@#$%^&*]/.test(password),
      "Must contain special character (!+_-.@#$%^&*)"
    )
    .optional(),
});

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  defaultInfo,
}) => {
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const form = useForm({
    mode: "uncontrolled",
  });

  const handleSubmit = async () => {
    const formData = form.getValues();

    // Error Handling
    const validationResult = await validateAsync(passwordValidationSchema)(
      formData
    );
    if (validationResult?.hasErrors) {
      console.error("Validation Errors", validationResult?.errors);
      form.setErrors(validationResult?.errors);
      enqueueSnackbar(
        "Password must be 7 or more characters and contain at least one lowercase letter, one uppercase letter, one number, and one special character (!+_-.@#$%^&*)",
        {
          variant: "error",
        }
      );
      return;
    }
    if (!defaultInfo?.email) {
      enqueueSnackbar(
        "An error occurred changing user password: invalid email.",
        {
          variant: "error",
        }
      );
      console.error("An error occurred changing user password: invalid email.");
      return;
    }

    // Update User Password
    await userApi.updateUser(defaultInfo?.user_id, {
      password: formData.password,
    });
    enqueueSnackbar("User Password Updated", {
      variant: "success",
    });
    LoggingAPI.createLog({
      user_id: currentUser.user_id,
      message: `Password changed: ${defaultInfo?.email}`,
      event: "user:change_password",
      object_type: "user",
    });
    onSubmit?.();
    onClose?.();
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <Modal
      open={show}
      onClose={onClose}
      style={{ position: "absolute", top: "30%", maxWidth: "500px" }}
    >
      <Modal.Title>Change User Password</Modal.Title>
      <div style={{ fontWeight: 400, margin: "20px 0px", width: "350px" }}>
        <TextInput
          size="sm"
          variant="outlined"
          label="Password"
          description="Change user password"
          type="password"
          key={form.key("password")}
          {...form.getInputProps("password")}
        />
      </div>

      <ButtonMenu>
        <Button size="xs" variant="subtle" onClick={handleCancel}>
          {"Cancel"}
        </Button>
        <Button
          size="xs"
          color={"primary"}
          variant="contained"
          onClick={handleSubmit}
        >
          {"Change Password"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

export default ChangePasswordModal;
